/**
 * /decks — aggregate list of every deck the user can see, grouped by
 * project. Each row links into the editor at /decks/:id.
 *
 * Empty groups (projects with no decks) are filtered out so the list
 * stays scannable.
 */

import { Link, useNavigate } from "react-router-dom";

import { useAllProjectDecks } from "../hooks";
import { SignOutButton } from "./SignOutButton";

export function DeckList() {
  const navigate = useNavigate();
  const { groups, isLoading, isError, error } = useAllProjectDecks();

  if (isError) {
    return (
      <PageShell>
        <ErrorBox
          title="Could not load decks"
          message={error instanceof Error ? error.message : String(error)}
        />
      </PageShell>
    );
  }

  if (isLoading && groups.length === 0) {
    return (
      <PageShell>
        <p style={{ color: "var(--gray-9)" }}>Loading projects…</p>
      </PageShell>
    );
  }

  const nonEmpty = groups.filter((g) => g.decks.length > 0);
  const totalDecks = nonEmpty.reduce((acc, g) => acc + g.decks.length, 0);

  return (
    <PageShell>
      <header style={headerStyle}>
        <div>
          <h1 style={{ margin: 0 }}>Decks</h1>
          <p style={{ margin: "0.25rem 0 0", color: "var(--gray-9)" }}>
            {totalDecks} {totalDecks === 1 ? "deck" : "decks"} across{" "}
            {nonEmpty.length} {nonEmpty.length === 1 ? "project" : "projects"}
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <button
            type="button"
            onClick={() => navigate("/decks/new")}
            style={primaryBtnStyle}
            data-testid="new-deck-button"
          >
            New deck
          </button>
          <SignOutButton />
        </div>
      </header>

      {nonEmpty.length === 0 ? (
        <EmptyState onCreate={() => navigate("/decks/new")} />
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {nonEmpty.map((group) => (
            <li
              key={group.project_id}
              style={{
                marginBottom: "2rem",
                borderTop: "1px solid var(--gray-5)",
                paddingTop: "1rem",
              }}
              data-testid={`deck-group-${group.project_id}`}
            >
              <h2 style={projectHeadingStyle}>{group.project_name}</h2>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {group.decks.map((d) => (
                  <li key={d.id} style={{ marginBottom: "0.5rem" }}>
                    <Link
                      to={`/decks/${d.id}`}
                      style={rowStyle}
                      data-testid={`deck-row-${d.id}`}
                    >
                      <span style={{ flex: 1, fontWeight: 500 }}>
                        {d.title}
                      </span>
                      <span style={metaStyle}>{d.slide_count} slides</span>
                      <span style={metaStyle}>{d.theme}</span>
                      <span style={metaStyle}>
                        v{d.version}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </PageShell>
  );
}

// ── Local component bits ───────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        maxWidth: 960,
        margin: "0 auto",
        padding: "2rem 1.5rem",
        color: "var(--gray-12)",
      }}
      data-testid="decks-list-page"
    >
      {children}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div
      style={{
        padding: "3rem 1rem",
        textAlign: "center",
        color: "var(--gray-9)",
      }}
      data-testid="decks-empty-state"
    >
      <p>No decks yet on any project you can see.</p>
      <button type="button" onClick={onCreate} style={primaryBtnStyle}>
        Create the first one
      </button>
    </div>
  );
}

function ErrorBox({ title, message }: { title: string; message: string }) {
  return (
    <div
      style={{
        padding: "1rem 1.25rem",
        background: "var(--red-3)",
        color: "var(--red-12)",
        border: "1px solid var(--red-7)",
        borderRadius: 6,
      }}
      data-testid="decks-error"
    >
      <strong>{title}</strong>
      <p style={{ margin: "0.5rem 0 0" }}>{message}</p>
    </div>
  );
}

// ── Inline styles (consistent with adjacent feature modules) ───────────────

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: "1.5rem",
};

const primaryBtnStyle: React.CSSProperties = {
  background: "var(--accent-9)",
  color: "white",
  border: "none",
  padding: "0.5rem 1rem",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.95em",
};

const projectHeadingStyle: React.CSSProperties = {
  fontSize: "1.05em",
  fontWeight: 600,
  color: "var(--gray-11)",
  margin: "0 0 0.5rem",
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  padding: "0.5rem 0.75rem",
  borderRadius: 4,
  color: "var(--gray-12)",
  textDecoration: "none",
  background: "var(--gray-2)",
};

const metaStyle: React.CSSProperties = {
  fontSize: "0.85em",
  color: "var(--gray-9)",
  fontVariantNumeric: "tabular-nums",
};
